import { useState, useEffect } from "react";
import type { Blog, UpdateBlogParams } from "../../types/blog";
import { Input } from "../../components/Input";
import { TextArea } from "../../components/TextArea";
import { Select } from "../../components/Select";
import { Button } from "../../components/Button";
import { CollapsibleSection } from "../../components/CollapsibleSection";

interface BlogDetailsPaneProps {
    blog: Blog;
    onUpdate: (id: string | number, params: UpdateBlogParams) => Promise<Blog>;
    isOpen?: boolean;
    onToggle?: () => void;
}

export function BlogDetailsPane({ blog, onUpdate, isOpen, onToggle }: BlogDetailsPaneProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState<UpdateBlogParams>({
        title: blog.title || "",
        slug: blog.slug || "",
        description: blog.description || "",
        status: blog.status || "draft",
        cover_image: blog.cover_image || "",
        thumbnail_image: blog.thumbnail_image || "",
        tags: blog.tags || {},
    });

    const [tagPairs, setTagPairs] = useState<{ key: string; value: string }[]>([]);

    useEffect(() => {
        setFormData({
            title: blog.title || "",
            slug: blog.slug || "",
            description: blog.description || "",
            status: blog.status || "draft",
            cover_image: blog.cover_image || "",
            thumbnail_image: blog.thumbnail_image || "",
            tags: blog.tags || {},
        });

        if (blog.tags) {
            const pairs = Object.entries(blog.tags).map(([k, v]) => ({ key: k, value: String(v) }));
            setTagPairs(pairs);
        } else {
            setTagPairs([]);
        }
    }, [blog]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setSuccess(false);
        setError(null);
    };

    const updateTagsInForm = (pairs: { key: string; value: string }[]) => {
        setSuccess(false);
        setError(null);
        const newTags: Record<string, string> = {};
        pairs.forEach(p => {
            if (p.key.trim() !== '') {
                newTags[p.key.trim()] = p.value.trim();
            }
        });
        setFormData(prev => ({ ...prev, tags: newTags }));
    };

    const handleTagChange = (index: number, field: 'key' | 'value', val: string) => {
        const newPairs = tagPairs.map((pair, i) => i === index ? { ...pair, [field]: val } : pair);
        setTagPairs(newPairs);
        updateTagsInForm(newPairs);
    };

    const handleAddTag = () => {
        setTagPairs([...tagPairs, { key: '', value: '' }]);
    };

    const handleRemoveTag = (index: number) => {
        const newPairs = tagPairs.filter((_, i) => i !== index);
        setTagPairs(newPairs);
        updateTagsInForm(newPairs);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccess(false);

        try {
            await onUpdate(blog.id, formData);
            setSuccess(true);
        } catch (err) {
            setError("Failed to update blog details.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <CollapsibleSection
            title="Blog Details"
            isOpen={isOpen}
            onToggle={onToggle}
        >
            <form onSubmit={handleSubmit} className="flex flex-col border-t border-base-content/5 pt-4">
                <div className="flex flex-col gap-4 px-1 mb-4">
                    <Input
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                    />

                    <Input
                        label="Slug"
                        name="slug"
                        value={formData.slug}
                        onChange={handleChange}
                    />

                    <TextArea
                        label="Description"
                        name="description"
                        value={formData.description || ""}
                        onChange={handleChange}
                        className="h-28 resize-none"
                    />

                    <Select
                        label="Status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                    >
                        <option value="draft">Draft</option>
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                    </Select>

                    <Input
                        label="Cover Image"
                        name="cover_image"
                        value={formData.cover_image || ""}
                        onChange={handleChange}
                    />
                    <Input
                        label="Thumbnail Image"
                        name="thumbnail_image"
                        value={formData.thumbnail_image || ""}
                        onChange={handleChange}
                    />

                    <div className="flex flex-col gap-2">
                        <label className="label pb-0">Tags</label>
                        <div className="flex flex-col gap-2">
                            {tagPairs.map((tag, index) => (
                                <div key={index} className="flex gap-2 items-center">
                                    <Input
                                        value={tag.key}
                                        onChange={(e) => handleTagChange(index, 'key', e.target.value)}
                                        placeholder="Key"
                                        containerClassName="flex-1 min-w-0"
                                    />
                                    <Input
                                        value={tag.value}
                                        onChange={(e) => handleTagChange(index, 'value', e.target.value)}
                                        placeholder="Value"
                                        containerClassName="flex-1 min-w-0"
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => handleRemoveTag(index)}
                                        className="btn-ghost btn-sm text-error px-2 shrink-0"
                                        title="Remove tag"
                                    >
                                        ×
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <Button
                            type="button"
                            onClick={handleAddTag}
                            className="btn-outline btn-sm w-full border-dashed opacity-50 hover:opacity-100"
                        >
                            + Add Tag
                        </Button>
                    </div>
                </div>

                <div className="flex flex-col gap-2 pt-3 border-t border-base-content/5">
                    {error && <div className="text-error text-xs">{error}</div>}
                    {success && <div className="text-success text-xs">Updated successfully!</div>}

                    <Button
                        type="submit"
                        className="btn-primary w-full"
                        isLoading={isLoading}
                    >
                        Save Details
                    </Button>
                </div>
            </form>
        </CollapsibleSection>
    );
}
